import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import {
  BoxSlotTemplateGroupFormikType,
  BoxSlotTemplateGroupInputKey,
} from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
  getSlotDurationOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import { word } from "@modules/core/utils/i18n"
import { AssignedCoachInput } from "@modules/formik/components/assignedCoachInput"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { HUText } from "@modules/ui/components/huText"
import { useTimeCharAndMasks } from "@modules/utils/dateUtils"
import { SimpleFormik } from "@src/typings/formik"
import { useTheme } from "styled-components"

export const BoxSlotTemplateGroupSettingsForm: React.FC<{
  formik: SimpleFormik<BoxSlotTemplateGroupFormikType, BoxSlotTemplateGroupInputKey>
  moveToScheduleStep: () => void
}> = ({ formik, moveToScheduleStep }) => {
  const theme = useTheme()
  const { hideSidebar } = useSidebar()

  const durationOptions = getSlotDurationOptions()
  const registrationBeginningOptions = getRegistrationBeginningOptions()
  const registrationEndOptions = getRegistrationEndOptions()
  const cancellationBeginningOptions = getCancellationBeginningOptions()
  const { dayChar, dayMask, hourChar, hourMask } = useTimeCharAndMasks()

  const toggleMultiBooking = () => {
    formik.setFieldValue("allowMultiBooking", !formik.values.allowMultiBooking)
  }

  const toggleDropIn = () => {
    formik.setFieldValue("dropinsAccepted", !formik.values.dropinsAccepted)
  }

  const toggleTrialSessions = () => {
    formik.setFieldValue("trialsAccepted", !formik.values.trialsAccepted)
  }

  return (
    <>
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM">{word("box.slotTemplateGroup.form.settings")}</HUText>
        <HUCardAlert
          title={""}
          description={word("box.slotTemplateGroup.form.defaultValuesExplanation")}
          icon={<InformationAlertIcon backgroundColor={theme.primaryColor100} color={theme.primaryColor600} />}
        />
      </div>

      <HUTimeDropdownOrCustomizedTimeInput
        name="slotDuration"
        label={word("box.slotTemplateGroup.form.slotDuration.label")}
        dropdownOptions={durationOptions}
        getFieldProps={() => formik.getFieldProps("slotDuration")}
        setFieldValue={formik.setFieldValue}
        isInvalid={isFormFieldInValid(`slotDuration`, formik)}
        error={getFormErrorMessage(`slotDuration`, formik)}
        isRequiredInput
        maskedInputProps={{
          placeholder: hourChar,
          slotChar: hourChar,
          mask: hourMask,
          onChange: (e) => {
            formik.setFieldValue("slotDuration", e.target.value)
          },
        }}
      />

      <FormikInput
        flex
        formikStyle={{ flexGrow: 1 }}
        isRequiredInput
        label={word("box.slotTemplateGroup.form.participantLimit.label")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("participantLimit", formik)}
        name="participantLimit"
        placeHolder={word("box.slotTemplateGroup.form.participantLimit.placeholder")}
        error={getFormErrorMessage("participantLimit", formik, { errorMessage: word("global.form.required") })}
        setFieldValue={formik.setFieldValue}
        type="number"
        rightButton={word("box.slotTemplateGroup.form.participantLimit.unit", {
          count: formik.values.participantLimit ?? 0,
        })}
        numberProps={{
          min: BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
          max: BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
          maxFractionDigits: 0,
        }}
      />

      <div className="flex flex-row justify-content-between align-items-center gap-2">
        {/* Drop in accepted */}
        <div
          className={`flex flex-column align-items-center justify-content-between w-full gap-2 py-3 px-2 align-self-stretch border-round border-solid cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.dropinsAccepted ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
          onClick={toggleDropIn}
        >
          {formik.values.dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
          <HUText
            fontStyle="TS"
            className="text-center"
            color={formik.values.dropinsAccepted ? theme.mainWhite : theme.neutralColor600}
          >
            {formik.values.dropinsAccepted
              ? word("box.slotTemplateGroup.form.dropIn.allowed")
              : word("box.slotTemplateGroup.form.dropIn.rejected")}
          </HUText>
        </div>

        {/* Trial session accepted*/}
        <div
          className={`flex flex-column align-items-center justify-content-between w-full gap-2 py-3 px-2 align-self-stretch border-round border-solid  cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.trialsAccepted ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
          onClick={toggleTrialSessions}
        >
          {formik.values.trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
          <HUText
            fontStyle="TS"
            className="text-center"
            color={formik.values.trialsAccepted ? theme.mainWhite : theme.neutralColor600}
          >
            {formik.values.trialsAccepted
              ? word("box.slotTemplateGroup.form.trialSession.allowed")
              : word("box.slotTemplateGroup.form.trialSession.rejected")}
          </HUText>
        </div>
      </div>

      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" className="uppercase">
          {word("box.slotTemplateGroup.form.registration.label")}
        </HUText>
        <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</HUText>
        <div className="flex gap-2">
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitStart"
            dropdownOptions={registrationBeginningOptions}
            getFieldProps={() => formik.getFieldProps("limitStart")}
            label={word("box.slotTemplateGroup.form.registration.beginning")}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid(`limitStart`, formik)}
            error={getFormErrorMessage(`limitStart`, formik)}
            maskedInputProps={{
              placeholder: dayChar,
              slotChar: dayChar,
              mask: dayMask,
              onChange: (e) => {
                formik.setFieldValue("limitStart", e.target.value)
              },
            }}
          />
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitEnd"
            dropdownOptions={registrationEndOptions}
            label={word("box.slotTemplateGroup.form.registration.end")}
            getFieldProps={() => formik.getFieldProps("limitEnd")}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid(`limitEnd`, formik)}
            error={getFormErrorMessage(`limitEnd`, formik)}
            maskedInputProps={{
              placeholder: dayChar,
              slotChar: dayChar,
              mask: dayMask,
              onChange: (e) => {
                formik.setFieldValue("limitEnd", e.target.value)
              },
            }}
          />
        </div>
      </div>

      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" className="uppercase">
          {word("box.slotTemplateGroup.form.cancellation.label")}
          <HUText fontStyle="LL" color={theme.necessaryInput}>
            {" *"}
          </HUText>
        </HUText>
        <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</HUText>
        <HUTimeDropdownOrCustomizedTimeInput
          name="limitCancel"
          dropdownOptions={cancellationBeginningOptions}
          label={word("box.slotTemplateGroup.form.cancellation.beginning")}
          getFieldProps={() => formik.getFieldProps("limitCancel")}
          setFieldValue={formik.setFieldValue}
          isInvalid={isFormFieldInValid(`limitCancel`, formik)}
          error={getFormErrorMessage(`limitCancel`, formik)}
          maskedInputProps={{
            placeholder: dayChar,
            slotChar: dayChar,
            mask: dayMask,
            onChange: (e) => {
              formik.setFieldValue("limitCancel", e.target.value)
            },
          }}
        />
      </div>

      <div
        className={`flex w-full gap-4 border-round border-solid p-4 cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.allowMultiBooking ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
        onClick={toggleMultiBooking}
      >
        <div className="flex flex-column w-9 gap-3 align-items-center ">
          {formik.values.allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
          <HUText
            fontStyle="TS"
            className="text-center"
            color={formik.values.allowMultiBooking ? theme.mainWhite : theme.neutralColor600}
          >
            {formik.values.allowMultiBooking
              ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
              : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
          </HUText>
        </div>
        <HUText
          fontStyle="BS"
          color={theme.neutralColor600}
          style={{ whiteSpace: "pre-line", overflowWrap: "break-word" }}
        >
          {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
        </HUText>
      </div>

      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" className="uppercase">
          {word("box.slotTemplateGroup.form.area.title")}
        </HUText>
        <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.area.subtitle")}</HUText>
        <FormikInput
          flex
          formikStyle={{ display: "flex", flexGrow: 1 }}
          name="location"
          isRequiredInput
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("location", formik)}
          setFieldValue={formik.setFieldValue}
          error={getFormErrorMessage("location", formik)}
          placeHolder={word("box.slotTemplateGroup.form.area.placeholder")}
        />
      </div>

      <div className="flex flex-column">
        <AssignedCoachInput
          label={word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
          textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
          name="assignedStaffMembers"
          isRequiredInput={false}
          isInvalid={isFormFieldInValid("assignedStaffMembers", formik)}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("assignedStaffMembers", formik)}
        />
      </div>

      <div className="flex justify-content-end mt-3 pb-4">
        <div className="flex column gap-3 sm:flex-row flex-grow-1">
          <HUButton
            type={"Default"}
            size="M"
            colorType="Quaternary"
            text={word("global.cancel")}
            onClick={() => hideSidebar()}
            className="w-full"
          />
          <HUButton
            type={"Right_Icon"}
            icon={{
              iconView: <span className={`p-button-icon p-c pi pi-arrow-right`} style={{ color: theme.mainWhite }} />,
            }}
            size="M"
            colorType="Primary"
            text={word("global.next")}
            className="mr-2 w-full"
            onClick={moveToScheduleStep}
          />
        </div>
      </div>
    </>
  )
}
